import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  CircularProgress,
  Alert,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  TextField,
  Button,
  Divider,
  Paper,
  Stack,
  LinearProgress,
  Snackbar,
  Chip,
} from "@mui/material";
import { useParams, useLocation } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import api from "../../utils/api";
import ElectionDocumentsViewer from "./ElectionDocumentsViewer";

const cardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const VotePage = () => {
  const { electionId } = useParams();
  const location = useLocation();

  const [election, setElection]   = useState(null);
  const [questions, setQuestions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [answers, setAnswers]     = useState({});
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack]         = useState({ open: false, message: "", severity: "error" });

  const [receiptCode, setReceiptCode] = useState("");
  const [receiptCopied, setReceiptCopied] = useState(false);
  const [requiresPin, setRequiresPin] = useState(false);
  const [pin, setPin]                 = useState("");   // the PIN that unlocked the ballot — resent on vote submit
  const [pinInput, setPinInput]       = useState("");
  const [pinError, setPinError]       = useState("");
  const [checkingPin, setCheckingPin] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("t") || searchParams.get("invite");
  const qr    = searchParams.get("qr") === "1";

  // Record invite open
  useEffect(() => {
    if (!token || !electionId) return;
    api
      .post(`/elections/${electionId}/share/opened`, { token, qr })
      .catch((err) => console.error("Failed to record open:", err));
  }, [token, electionId, qr]);

  // Load ballot. use public endpoint so no login is required
  const loadBallot = async (pinToTry) => {
    if (!token) {
      setError("No ballot token found in this link. Please use the link sent to you.");
      setLoading(false);
      return;
    }
    try {
      const res = await api.post(`/public/ballot/${electionId}`, {
        token,
        ...(pinToTry ? { pin: pinToTry } : {}),
      });

      if (res.data.requiresPin) {
        setRequiresPin(true);
        if (pinToTry) setPinError("Incorrect PIN. Please try again.");
        return;
      }

      setRequiresPin(false);
      if (pinToTry) setPin(pinToTry);
      setElection(res.data.election);
      setQuestions(res.data.questions || []);
      setDocuments(res.data.documents || []);
      if (res.data.alreadyVoted) {
        setSubmitted(true);   // show "already voted" state
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.message || "Unable to load election ballot.";
      if (err.response?.data?.requiresPin) {
        setRequiresPin(true);
        setPinError(msg);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
      setCheckingPin(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadBallot();
  }, [electionId, token]); // eslint-disable-line

  const handlePinSubmit = () => {
    if (!pinInput.trim()) return;
    setPinError("");
    setCheckingPin(true);
    loadBallot(pinInput.trim());
  };

  // ── Answer handlers ────────────────────────────────────────────────────────
  const handleSingle = (qId, value) =>
    setAnswers((prev) => ({ ...prev, [qId]: value }));

  const handleCheckbox = (qId, optId) =>
    setAnswers((prev) => {
      const arr = prev[qId] || [];
      return arr.includes(optId)
        ? { ...prev, [qId]: arr.filter((x) => x !== optId) }
        : { ...prev, [qId]: [...arr, optId] };
    });

  const handleText = (qId, value) =>
    setAnswers((prev) => ({ ...prev, [qId]: value }));

  // ── Validation ─────────────────────────────────────────────────────────────
  const validate = () => {
    for (const q of questions) {
      if (!q.is_required) continue;
      const ans = answers[q.id];
      if (q.question_type === "multiple") {
        if (!ans || ans.length === 0) return q.question_text;
      } else if (q.question_type === "text") {
        if (!ans || String(ans).trim() === "") return q.question_text;
      } else {
        // single / yesno
        if (ans === undefined || ans === null || ans === "") return q.question_text;
      }
    }
    return null;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const submitVote = async () => {
    const missing = validate();
    if (missing) {
      setSnack({
        open: true,
        message: `Please answer the required question: "${missing}"`,
        severity: "warning",
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post(`/public/vote/${electionId}`, {
        token,
        answers,
        ...(pin ? { pin } : {}),
      });
      setSubmitted(true);
      if (res.data?.receiptCode) {
        setReceiptCode(res.data.receiptCode);
      }
    } catch (err) {
      console.error("Vote submit error:", err);
      setSnack({
        open: true,
        message: err.response?.data?.message || "Failed to submit vote. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Progress ───────────────────────────────────────────────────────────────
  const answeredCount = questions.filter((q) => {
    const a = answers[q.id];
    if (q.question_type === "multiple") return a && a.length > 0;
    if (q.question_type === "text")     return a && String(a).trim() !== "";
    return a !== undefined && a !== null && a !== "";
  }).length;

  const progress = questions.length > 0 ? (answeredCount / questions.length) * 100 : 0;

  // ── Render question ────────────────────────────────────────────────────────
  const renderQuestion = (q) => {
    const qId = q.id;
    switch (q.question_type) {
      case "single":
        return (
          <RadioGroup
            value={answers[qId] ?? ""}
            onChange={(e) => handleSingle(qId, Number(e.target.value))}
          >
            {q.options.map((opt) => (
              <FormControlLabel
                key={opt.id}
                value={opt.id}
                control={<Radio />}
                label={opt.option_text}
              />
            ))}
          </RadioGroup>
        );

      case "multiple":
        return (
          <Box>
            {q.options.map((opt) => {
              const selectedArr = answers[qId] || [];
              return (
                <Box key={opt.id} sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                  <Checkbox
                    checked={selectedArr.includes(opt.id)}
                    onChange={() => handleCheckbox(qId, opt.id)}
                  />
                  <Typography>{opt.option_text}</Typography>
                </Box>
              );
            })}
          </Box>
        );

      case "yesno": {
        // Yes/No questions have real ElectionOptions rows (sort_order 0 = Yes,
        // 1 = No) — behaves exactly like "single" once resolved to option IDs.
        const yesOpt = q.options?.find((o) => o.sort_order === 0) ?? q.options?.[0];
        const noOpt  = q.options?.find((o) => o.sort_order === 1) ?? q.options?.[1];
        return (
          <RadioGroup
            value={answers[qId] ?? ""}
            onChange={(e) => handleSingle(qId, Number(e.target.value))}
          >
            <FormControlLabel value={yesOpt?.id} control={<Radio />} label="Yes" />
            <FormControlLabel value={noOpt?.id}  control={<Radio />} label="No" />
          </RadioGroup>
        );
      }

      case "text":
      default:
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            value={answers[qId] || ""}
            onChange={(e) => handleText(qId, e.target.value)}
            placeholder="Enter your answer..."
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
        );
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          backgroundColor: "#f8fafc",
        }}
      >
        <Card elevation={0} sx={{ ...cardSx, p: 4, textAlign: "center", minWidth: 280 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2, color: "#6b778c" }}>Loading ballot…</Typography>
        </Card>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: "100vh", p: 3, backgroundColor: "#f8fafc" }}>
        <Box sx={{ maxWidth: 850, mx: "auto" }}>
          <Alert severity="error" sx={{ borderRadius: "12px" }}>{error}</Alert>
        </Box>
      </Box>
    );
  }

  if (requiresPin && !election) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          backgroundColor: "#f8fafc",
        }}
      >
        <Card elevation={0} sx={{ ...cardSx, p: 4, maxWidth: 380, width: "100%" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Enter Your Voting PIN
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
            This election requires a PIN in addition to your ballot link. Check the
            message you received with your PIN, or contact your association manager.
          </Typography>

          <TextField
            fullWidth
            label="PIN"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handlePinSubmit(); }}
            error={!!pinError}
            helperText={pinError || " "}
            autoFocus
            sx={{ mb: 1, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />

          <Button
            fullWidth
            variant="contained"
            onClick={handlePinSubmit}
            disabled={checkingPin || !pinInput.trim()}
            sx={{
              backgroundColor: "#2f63ad",
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { backgroundColor: "#275592", boxShadow: "none" },
            }}
          >
            {checkingPin ? "Checking…" : "Continue"}
          </Button>
        </Card>
      </Box>
    );
  }

  if (!election) {
    return (
      <Box sx={{ minHeight: "100vh", p: 3, backgroundColor: "#f8fafc" }}>
        <Box sx={{ maxWidth: 850, mx: "auto" }}>
          <Alert severity="warning" sx={{ borderRadius: "12px" }}>Election not found.</Alert>
        </Box>
      </Box>
    );
  }

  // ── Thank-you screen ───────────────────────────────────────────────────────
  if (submitted) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 5,
          px: 2,
        }}
      >
        <Card elevation={0} sx={{ ...cardSx, maxWidth: 560, width: "100%", overflow: "hidden" }}>
          <Box
            sx={{
              px: 4,
              pt: 5,
              pb: 4,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                backgroundColor: "rgba(46,125,50,0.10)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2.5,
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "#2e7d32" }} />
            </Box>

            <Typography variant="h5" sx={{ fontWeight: 800, color: "#223142", mb: 1 }}>
              Vote Submitted!
            </Typography>

            <Typography variant="body1" sx={{ color: "#6b778c", mb: 0.5 }}>
              Your vote for
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: "#223142", mb: 2 }}>
              {election.title}
            </Typography>

            <Typography variant="body2" sx={{ color: "#9eaab5" }}>
              has been recorded securely. Thank you for participating.
            </Typography>

            {receiptCode && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: "rgba(47,99,173,0.04)",
                  border: "1px solid rgba(47,99,173,0.12)",
                }}
              >
                <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 700, display: "block", mb: 0.5 }}>
                  YOUR RECEIPT CODE: SAVE THIS FOR YOUR RECORDS
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "monospace",
                      fontWeight: 800,
                      fontSize: "1.1rem",
                      color: "#223142",
                      letterSpacing: 0.5,
                    }}
                  >
                    {receiptCode}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      navigator.clipboard?.writeText(receiptCode);
                      setReceiptCopied(true);
                      setTimeout(() => setReceiptCopied(false), 2000);
                    }}
                    sx={{ textTransform: "none", fontWeight: 700, borderRadius: "8px", color: "#2f63ad" }}
                  >
                    {receiptCopied ? "Copied!" : "Copy"}
                  </Button>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <HowToVoteIcon sx={{ fontSize: 16, color: "#b0bac6" }} />
              <Typography variant="caption" sx={{ color: "#b0bac6" }}>
                E-Voting System · Secure &amp; Confidential
              </Typography>
            </Box>
          </Box>
        </Card>
      </Box>
    );
  }

  // ── Ballot ─────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        py: { xs: 3, md: 5 },
        px: 2,
      }}
    >
      <Card elevation={0} sx={{ ...cardSx, maxWidth: 900, mx: "auto", overflow: "hidden" }}>
        {/* Header */}
        <Box
          sx={{
            px: { xs: 2.5, md: 4 },
            pt: { xs: 3, md: 4 },
            pb: 2.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, color: "#223142", mb: 0.75 }}>
            {election.title}
          </Typography>

          {election.description && (
            <Typography variant="body2" sx={{ color: "#5a6578", mb: 1.5, lineHeight: 1.6 }}>
              {election.description}
            </Typography>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label="Secure Ballot"
              size="small"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                backgroundColor: "rgba(47,99,173,0.08)",
                color: "#2f63ad",
                borderRadius: "999px",
              }}
            />
            <Typography variant="body2" sx={{ color: "#9eaab5" }}>
              Election #{election.id}
            </Typography>
          </Box>
        </Box>

        {documents.length > 0 && (
          <Box sx={{ px: { xs: 2.5, md: 4 }, pt: 2.5 }}>
            <ElectionDocumentsViewer documents={documents} />
          </Box>
        )}

        {/* Progress bar */}
        <Box sx={{ px: { xs: 2.5, md: 4 }, pt: 2.5, pb: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
            <Typography variant="body2" sx={{ color: "#6b778c", fontWeight: 600 }}>
              Progress
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              {answeredCount} / {questions.length} answered
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: "999px",
              backgroundColor: "rgba(47,99,173,0.08)",
              "& .MuiLinearProgress-bar": {
                borderRadius: "999px",
                backgroundColor: progress === 100 ? "#2e7d32" : "#2f63ad",
              },
            }}
          />
        </Box>

        {/* Questions */}
        <Box sx={{ p: { xs: 2.5, md: 4 } }}>
          <Stack spacing={2.5}>
            {questions.map((q, index) => {
              const answered = (() => {
                const a = answers[q.id];
                if (q.question_type === "multiple") return a && a.length > 0;
                if (q.question_type === "text")     return a && String(a).trim() !== "";
                return a !== undefined && a !== null && a !== "";
              })();

              return (
                <Paper
                  key={q.id}
                  elevation={0}
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: "16px",
                    border: `1px solid ${answered ? "rgba(46,125,50,0.20)" : "rgba(47,99,173,0.08)"}`,
                    boxShadow: "0 4px 14px rgba(0,0,0,0.04)",
                    backgroundColor: answered ? "rgba(46,125,50,0.02)" : "rgba(255,255,255,0.98)",
                    transition: "border-color 0.2s, background-color 0.2s",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5, gap: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: "#223142",
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 0.5,
                        flex: 1,
                      }}
                    >
                      <span>{index + 1}. {q.question_text}</span>
                      {q.is_required && (
                        <Box component="span" sx={{ color: "error.main", ml: 0.25 }}>*</Box>
                      )}
                    </Typography>

                    {answered && (
                      <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "#2e7d32", flexShrink: 0, mt: 0.3 }} />
                    )}
                  </Box>

                  <Divider sx={{ mb: 2 }} />
                  <Box>{renderQuestion(q)}</Box>
                </Paper>
              );
            })}
          </Stack>

          {questions.some((q) => q.is_required) && (
            <Typography variant="caption" sx={{ display: "block", mt: 2, color: "#9eaab5" }}>
              * Required question
            </Typography>
          )}

          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={submitVote}
              disabled={submitting}
              sx={{
                backgroundColor: "#2f63ad",
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                px: 3,
                py: 1.1,
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#275592",
                  boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                },
              }}
            >
              {submitting ? "Submitting…" : "Submit Vote"}
            </Button>
          </Box>
        </Box>
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((p) => ({ ...p, open: false }))}
          sx={{ borderRadius: "12px" }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default VotePage;
